import type { InspectorDTO } from 'src/types/dto/inspector.dto';

import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';

import { DirectionsButton } from 'src/components/map/directions-button';
import { getInspectorDistanceDisplay } from 'src/utils/inspector-distance';

type InspectorDistanceCellProps = {
  row: Pick<
    InspectorDTO,
    'location' | 'travelDistance' | 'travelTime' | 'straightLineDistance'
  > & {
    hotelNights?: number;
  };
  siteLocation?: string | null;
  isDeskService?: boolean;
  distanceLoading?: boolean;
  showDirections?: boolean;
  compactLoading?: boolean;
};

function InspectorDistanceCellComponent({
  row,
  siteLocation,
  isDeskService = false,
  distanceLoading = false,
  showDirections = true,
  compactLoading = false,
}: InspectorDistanceCellProps) {
  const { t } = useTranslation(['activities', 'common']);

  const distanceI18n = useMemo(
    () => ({
      deskServiceZeroKm: t('distance.zeroKm', { ns: 'common' }),
      travelDistance: (distanceLabel: string) =>
        t('allocation.modal.distanceTooltip.travelDistance', { distance: distanceLabel }),
      straightLineDistance: (distanceLabel: string) =>
        t('allocation.modal.distanceTooltip.straightLineDistance', { distance: distanceLabel }),
      travelTime: (timeLabel: string) =>
        t('allocation.modal.distanceTooltip.travelTime', { time: timeLabel }),
      travelTimeHoursMinutes: (hours: number, minutes: number) =>
        t('allocation.modal.distanceTooltip.travelTimeHoursMinutes', { hours, minutes }),
      travelTimeHoursOnly: (hours: number) =>
        t('allocation.modal.distanceTooltip.travelTimeHoursOnly', { hours }),
      travelTimeMinutesOnly: (minutes: number) =>
        t('allocation.modal.distanceTooltip.travelTimeMinutesOnly', { minutes }),
    }),
    [t],
  );

  if (distanceLoading) {
    if (compactLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <CircularProgress size={14} color="inherit" />
        </Box>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary" noWrap>
        {t('allocation.modal.loadingText')}
      </Typography>
    );
  }

  const { primaryLabel, tooltipLabel } = getInspectorDistanceDisplay(
    {
      travelDistance: row.travelDistance,
      travelTime: row.travelTime,
      straightLineDistance: row.straightLineDistance,
      inspectorLocation: row.location,
      siteLocation,
      isDeskService,
    },
    distanceI18n,
  );

  const distanceContent = (
    <Typography
      variant="body2"
      noWrap
      sx={
        tooltipLabel
          ? {
              cursor: 'help',
              borderBottom: '1px dotted',
              borderColor: 'divider',
              display: 'inline-block',
            }
          : undefined
      }
    >
      {primaryLabel ?? (isDeskService ? distanceI18n.deskServiceZeroKm : '—')}
    </Typography>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: showDirections ? 'space-between' : 'flex-start',
        width: '100%',
        minWidth: 0,
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {tooltipLabel ? (
          <Tooltip
            title={tooltipLabel}
            placement="top"
            arrow
            disableInteractive
            enterDelay={500}
            enterNextDelay={300}
            slotProps={{
              popper: {
                sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
              },
            }}
          >
            <Box component="span" sx={{ display: 'inline-block', maxWidth: '100%' }}>
              {distanceContent}
            </Box>
          </Tooltip>
        ) : (
          distanceContent
        )}
        {+(row.hotelNights ?? 0) > 0 && (
          <Typography variant="subtitle2" color="text.secondary" noWrap>
            {t('allocation.details.activity.nights', { count: row.hotelNights })}
          </Typography>
        )}
      </Box>
      {showDirections && row.location && siteLocation && (
        <DirectionsButton origin={row.location} destination={siteLocation} />
      )}
    </Box>
  );
}

export const InspectorDistanceCell = memo(InspectorDistanceCellComponent);
